import React, { useId, useState } from "react";
import { Button } from "./ui/button";
import { DatePicker } from "./ui/date-picker";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Label } from "./ui/label";
import fetchWithAuth from "../fetchWithAuth";
import { Download, FileSpreadsheet } from "lucide-react";

/**
 * Блок экспорта отчёта в Excel.
 * @param {Object} props
 * @param {number|string} props.projectId
 * @param {string} props.className - Дополнительные классы для Card
 * @param {boolean} props.noMarginBottom - Если true, то не добавляет отступ снизу (mb-6)
 */
export default function ReportExportBlock({ projectId, groupId, className, noMarginBottom = false, compact = false }) {
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputId = useId();
  const invalidPeriod = dateFrom && dateTo && dateFrom > dateTo;

  const handleExport = async () => {
    setError("");
    if (!dateFrom || !dateTo || invalidPeriod) {
      setError("Выберите корректный период отчёта");
      return;
    }
    
    // Форматирование дат в формат YYYY-MM-DD
    const formatDate = (date) => {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setLoading(true);
    try {
      // Запрос на backend для выгрузки отчёта
      const params = new URLSearchParams({
        ...(groupId != null ? { group: groupId } : { projectId }),
        dateFrom: formatDate(dateFrom),
        dateTo: formatDate(dateTo),
      });
      const res = await fetchWithAuth(`/api/report/${groupId != null ? "group/" : ""}export?${params.toString()}`, {
        method: "GET",
        headers: {
          // авторизация если нужна
        },
      });
      if (!res.ok) {
        const response = await res.json().catch(() => ({}));
        throw new Error(response.error || "Ошибка выгрузки отчёта");
      }
      const blob = await res.blob();
      // Скачивание файла
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_${groupId != null ? `group${groupId}_` : ""}${formatDate(dateFrom)}_${formatDate(dateTo)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(error.message || "Ошибка при скачивании отчёта");
    }
    setLoading(false);
  };

  if (compact) return <section aria-label="Экспорт отчёта" className={`rounded-xl border border-emerald-100 bg-emerald-50/60 px-3 py-2 shadow-sm ${className || ""}`}>
    <div className="mb-2 flex items-center justify-between gap-6">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800"><FileSpreadsheet className="h-3.5 w-3.5" />Экспорт отчёта</h3>
      <span className="text-[10px] font-semibold tracking-wide text-emerald-600">XLSX</span>
    </div>
    <div className="flex items-center gap-2">
      <DatePicker id={`${inputId}-from`} ariaLabel="Начальная дата отчёта" placeholder="С даты" value={dateFrom} onChange={setDateFrom}
        className="h-8 w-[112px] border-emerald-200 bg-white px-2 text-xs [&_svg]:mr-1.5 [&_svg]:h-3.5 [&_svg]:w-3.5" />
      <DatePicker id={`${inputId}-to`} ariaLabel="Конечная дата отчёта" placeholder="По дату" value={dateTo} onChange={setDateTo}
        className="h-8 w-[112px] border-emerald-200 bg-white px-2 text-xs [&_svg]:mr-1.5 [&_svg]:h-3.5 [&_svg]:w-3.5" />
      <Button type="button" onClick={handleExport} disabled={loading || !dateFrom || !dateTo || invalidPeriod} title="Выгрузить отчёт" aria-label="Выгрузить отчёт"
        className="h-8 w-9 border border-emerald-600 bg-emerald-600 p-0 hover:bg-emerald-700">
        <Download className={`h-4 w-4 ${loading ? "animate-pulse" : ""}`} />
      </Button>
    </div>
    {(error || invalidPeriod) && <p role="alert" className="mt-2 max-w-xs text-xs text-red-600">{invalidPeriod ? "Начальная дата не должна быть позже конечной" : error}</p>}
  </section>;

  return (
    <Card className={`${noMarginBottom ? '' : 'mb-6'} ${className || ''}`}>
      <CardHeader>
        <CardTitle>Экспорт отчёта</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-end gap-4">
          <div className="grid w-[180px] items-center gap-1.5">
            <Label htmlFor={`${inputId}-from`}>Начальная дата</Label>
            <DatePicker
              id={`${inputId}-from`}
              value={dateFrom}
              onChange={setDateFrom}
              className="w-full"
            />
          </div>
          <div className="grid w-[180px] items-center gap-1.5">
            <Label htmlFor={`${inputId}-to`}>Конечная дата</Label>
            <DatePicker
              id={`${inputId}-to`}
              value={dateTo}
              onChange={setDateTo}
              className="w-full"
            />
          </div>
          <div className="flex items-end">
            <Button
              onClick={handleExport}
              disabled={loading || !dateFrom || !dateTo || invalidPeriod}
              className="h-10"
            >
              {loading ? "Выгрузка..." : "Выгрузить отчёт"}
            </Button>
          </div>
        </div>
        {(error || invalidPeriod) && <p role="alert" className="mt-2 text-sm text-red-600">{invalidPeriod ? "Начальная дата не должна быть позже конечной" : error}</p>}
      </CardContent>
    </Card>
  );
}
